import type {
	UnwrapRefCarouselApi as CarouselApi,
	CarouselEmits,
	CarouselProps
} from './interface';

import { createInjectionState } from '@vueuse/core';
import emblaCarouselVue from 'embla-carousel-vue';
import { ref, watch } from 'vue';

const [useProvideCarousel, useInjectCarousel] = createInjectionState(
	({ opts, orientation, plugins }: CarouselProps, emits: CarouselEmits) => {
		const [emblaNode, emblaApi] = emblaCarouselVue(
			{
				...opts,
				axis: orientation === 'horizontal' ? 'x' : 'y'
			},
			plugins
		);

		function scrollPrev() {
			emblaApi.value?.scrollPrev();
		}
		function scrollNext() {
			emblaApi.value?.scrollNext();
		}

		const canScrollNext = ref(false);
		const canScrollPrev = ref(false);
		let initializedApi: CarouselApi | undefined;

		function onSelect(api: CarouselApi) {
			canScrollNext.value = api?.canScrollNext() || false;
			canScrollPrev.value = api?.canScrollPrev() || false;
		}

		function setupCarouselApi(api: CarouselApi) {
			if (!api || initializedApi === api) return;

			initializedApi?.off('init', onSelect);
			initializedApi?.off('reInit', onSelect);
			initializedApi?.off('select', onSelect);

			initializedApi = api;

			api.on('init', onSelect);
			api.on('reInit', onSelect);
			api.on('select', onSelect);
			onSelect(api);

			emits('init-api', api);
		}

		watch(emblaApi, setupCarouselApi, { immediate: true });

		function disableCarousel() {
			emblaApi.value?.reInit({
				watchDrag: false
			});
		}

		function enableCarousel() {
			emblaApi.value?.reInit({
				watchDrag: true
			});
		}

		return {
			carouselRef: emblaNode,
			carouselApi: emblaApi,
			canScrollPrev,
			canScrollNext,
			scrollPrev,
			scrollNext,
			orientation,
			disableCarousel,
			enableCarousel
		};
	}
);

function useCarousel() {
	const carouselState = useInjectCarousel();

	if (!carouselState) {
		throw new Error('useCarousel must be used within a <Carousel />');
	}

	return carouselState;
}

export { useCarousel, useProvideCarousel };
